import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { Trash2, UserX } from "lucide-react";
import { Button } from "../../../ui/components/Button";
import { ConfirmModal } from "../../../ui/components/ConfirmModal";
import { DisabledAccountStatusPanel } from "../common/DisabledAccountStatusPanel";
import * as adminUsersApi from "../../lib/api/admin-users";
import { getApiErrorMessage } from "../../lib/api/api-error";
import type { DisabledBySnapshot } from "../../lib/disabled-status";
import { useAuth } from "../../lib/auth/hooks";
import { getAccountAppOrigin } from "../../lib/auth/app-origins";
import * as styles from "./UserAccountDangerZone.css";

export type UserAccountDangerZoneMode = "admin" | "self";

type PendingAction = "disable" | "delete" | "restore";

const HARD_DELETE_CONFIRM_PHRASE = "delete";

export interface UserAccountDangerZoneProps {
  mode: UserAccountDangerZoneMode;
  userId: string;
  userEmail: string;
  userLabel: string;
  accessToken: string;
  isDisabled: boolean;
  disabledSnapshot?: DisabledBySnapshot;
  canDisable: boolean;
  canHardDelete: boolean;
  canRestore: boolean;
  /** Nested inside a parent Security section (e.g. profile page). */
  embedded?: boolean;
  onAccountChanged?: () => void;
}

function getErrorMessage(e: unknown, fallback: string): string {
  return getApiErrorMessage(e, fallback);
}

function dangerZoneDescription(mode: UserAccountDangerZoneMode): string {
  if (mode === "self") {
    return "Disable or permanently delete your Lion Pro account.";
  }
  return "Disable or permanently delete this user account.";
}

export function UserAccountDangerZone({
  mode,
  userId,
  userEmail,
  userLabel,
  accessToken,
  isDisabled,
  disabledSnapshot,
  canDisable,
  canHardDelete,
  canRestore,
  embedded = false,
  onAccountChanged,
}: UserAccountDangerZoneProps) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [pendingAction, setPendingAction] = React.useState<PendingAction | null>(
    null,
  );
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [expanded, setExpanded] = React.useState(false);

  const closeModal = React.useCallback(() => {
    if (loading) return;
    setPendingAction(null);
    setError(null);
  }, [loading]);

  const handleConfirm = React.useCallback(async () => {
    if (!pendingAction) return;
    setLoading(true);
    setError(null);
    try {
      if (pendingAction === "disable") {
        if (mode === "self") {
          await adminUsersApi.disableMyAccount(accessToken);
          await logout();
          window.location.href = `${getAccountAppOrigin()}/login`;
          return;
        }
        await adminUsersApi.disableUser(accessToken, userId);
      } else if (pendingAction === "delete") {
        if (mode === "self") {
          await adminUsersApi.deleteMyAccount(accessToken);
          await logout();
          window.location.href = `${getAccountAppOrigin()}/login`;
          return;
        }
        await adminUsersApi.deleteUserPermanently(accessToken, userId);
        navigate({ to: "/workspace/users", replace: true });
        return;
      } else {
        await adminUsersApi.restoreUser(accessToken, userId);
      }
      setPendingAction(null);
      onAccountChanged?.();
    } catch (e) {
      setError(getErrorMessage(e, "Something went wrong. Try again."));
    } finally {
      setLoading(false);
    }
  }, [
    pendingAction,
    mode,
    accessToken,
    userId,
    logout,
    navigate,
    onAccountChanged,
  ]);

  const modalCopy = React.useMemo(() => {
    if (!pendingAction) return null;
    const subject =
      mode === "self" ? "your account" : userLabel || userEmail;

    if (pendingAction === "disable") {
      return {
        title:
          mode === "self" ? "Disable your account?" : "Disable user account?",
        body:
          mode === "self"
            ? "Your account will be disabled. You will be signed out and will not be able to sign in again until an administrator restores your account. Your data and organization relationships will be kept."
            : `Disable ${subject}? They will be signed out, hidden from user lists for most users, and unable to sign in. Their data and organization relationships will be kept. An administrator can restore the account later.`,
        confirmLabel: "Disable account",
        confirmColor: "danger" as const,
      };
    }
    if (pendingAction === "delete") {
      return {
        title:
          mode === "self"
            ? "Permanently delete your account?"
            : "Permanently delete user account?",
        body:
          mode === "self"
            ? "This permanently removes your account and all related data. This cannot be undone. Your email address will become available for a new registration."
            : `Permanently delete ${subject}? This removes all data relations and cannot be undone. The email address will become available again for a new user to register with.`,
        confirmLabel: "Delete permanently",
        confirmColor: "danger" as const,
      };
    }
    return {
      title: "Restore user account?",
      body: `Restore ${userLabel || userEmail}? They will be able to sign in again after setting a new password via the reclaim email that is sent.`,
      confirmLabel: "Restore account",
      confirmColor: "primary" as const,
    };
  }, [pendingAction, mode, userLabel, userEmail]);

  const disableLabel =
    mode === "self" ? "Disable my account" : "Disable user account";
  const deleteLabel =
    mode === "self" ? "Delete my account" : "Delete user account";

  if (isDisabled) {
    const disabledPanel = (
      <DisabledAccountStatusPanel
        kind="user"
        snapshot={disabledSnapshot ?? {}}
        canRestore={canRestore}
        onRestore={
          canRestore
            ? () => {
                setError(null);
                setPendingAction("restore");
              }
            : undefined
        }
      />
    );

    if (!canRestore && !canHardDelete) {
      return disabledPanel;
    }

    return (
      <>
        {disabledPanel}
        {canHardDelete && (
          <div
            className={`${styles.dangerZone} ${embedded ? styles.dangerZoneEmbedded : ""}`}
            style={{ marginTop: 16 }}
          >
            <button type="button" className={styles.dangerZoneToggleBtn} onClick={() => setExpanded(e => !e)} aria-expanded={expanded}>
              Danger zone
              <svg className={`${styles.dangerZoneCaret} ${expanded ? styles.dangerZoneCaretOpen : ''}`} width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9" /></svg>
            </button>
            {expanded && <>
            <p className={styles.dangerZoneDescription}>
              Permanently delete this account and all related data.
            </p>
            <div className={styles.dangerZoneActions}>
              <Button
                type="button"
                color="danger"
                variant="solid"
                iconLeft={<Trash2 size={16} />}
                onClick={() => {
                  setError(null);
                  setPendingAction("delete");
                }}
              >
                {deleteLabel}
              </Button>
            </div>
            </>}
          </div>
        )}
        {pendingAction && modalCopy && (
          <ConfirmModal
            open
            title={modalCopy.title}
            body={modalCopy.body}
            confirmLabel={modalCopy.confirmLabel}
            confirmColor={modalCopy.confirmColor}
            confirmPhrase={
              pendingAction === "delete" ? HARD_DELETE_CONFIRM_PHRASE : undefined
            }
            loading={loading}
            error={error}
            onCancel={closeModal}
            onConfirm={() => void handleConfirm()}
          />
        )}
      </>
    );
  }

  if (!canDisable && !canHardDelete) {
    return null;
  }

  return (
    <>
      <div
        className={`${styles.dangerZone} ${embedded ? styles.dangerZoneEmbedded : ""}`}
      >
        <button type="button" className={styles.dangerZoneToggleBtn} onClick={() => setExpanded(e => !e)} aria-expanded={expanded}>
          Danger zone
          <svg className={`${styles.dangerZoneCaret} ${expanded ? styles.dangerZoneCaretOpen : ''}`} width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9" /></svg>
        </button>
        {expanded && <>
        <p className={styles.dangerZoneDescription}>
          {dangerZoneDescription(mode)}
        </p>
        <div className={styles.dangerZoneActions}>
          {canDisable && (
            <Button
              type="button"
              color="danger"
              variant="outline"
              iconLeft={<UserX size={16} />}
              onClick={() => {
                setError(null);
                setPendingAction("disable");
              }}
            >
              {disableLabel}
            </Button>
          )}
          {canHardDelete && (
            <Button
              type="button"
              color="danger"
              variant="solid"
              iconLeft={<Trash2 size={16} />}
              onClick={() => {
                setError(null);
                setPendingAction("delete");
              }}
            >
              {deleteLabel}
            </Button>
          )}
        </div>
        </>}
      </div>
      {pendingAction && modalCopy && (
        <ConfirmModal
          open
          title={modalCopy.title}
          body={modalCopy.body}
          confirmLabel={modalCopy.confirmLabel}
          confirmColor={modalCopy.confirmColor}
          confirmPhrase={
            pendingAction === "delete" ? HARD_DELETE_CONFIRM_PHRASE : undefined
          }
          loading={loading}
          error={error}
          onCancel={closeModal}
          onConfirm={() => void handleConfirm()}
        />
      )}
    </>
  );
}
